import { emptyFragment } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { programsStore } from '../../../ProgramsStore';
import { T } from '../../../Translations';
import ActivePrograms from '../ActivePrograms';
import CompletedPrograms from '../CompletedPrograms';
import ExpiredPrograms from '../ExpiredPrograms';
import UpcomingPrograms from '../UpcomingPrograms';
import { defaultProgramTab, programTabCount, programTabFromSearch } from './Functions';
import {
  ProgramTabKind,
  programTabId,
  programTabKinds,
  programTabLabel,
  programTabPanelId,
} from './Types';

const ProgramTabPanel: React.FC<{ kind: ProgramTabKind }> = ({ kind }) => {
  switch (kind) {
    case 'active':
      return <ActivePrograms />;
    case 'upcoming':
      return <UpcomingPrograms />;
    case 'graduated':
      return <CompletedPrograms />;
    case 'past':
      return <ExpiredPrograms />;
  }
};

const ProgramTabs: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const tabRefs = React.useRef<Partial<Record<ProgramTabKind, HTMLButtonElement | null>>>({});

  const selectTab = (kind: ProgramTabKind) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', kind);
    history.replace({ ...location, search: params.toString() });
  };

  const handleKeyDown = (event: React.KeyboardEvent, current: ProgramTabKind) => {
    const index = programTabKinds.indexOf(current);
    const last = programTabKinds.length - 1;
    const nextIndex = (): Maybe<number> => {
      switch (event.key) {
        case 'ArrowRight':
          return just(index === last ? 0 : index + 1);
        case 'ArrowLeft':
          return just(index === 0 ? last : index - 1);
        case 'Home':
          return just(0);
        case 'End':
          return just(last);
        default:
          return nothing();
      }
    };
    nextIndex()
      .map((i) => programTabKinds[i])
      .do((next) => {
        event.preventDefault();
        selectTab(next);
        tabRefs.current[next]?.focus();
      });
  };

  return programsStore.resource
    .map(({ payload }) => {
      const selected = programTabFromSearch(location.search).getOrElse(() =>
        defaultProgramTab(payload.programs),
      );

      return (
        <>
          {/* At 1140px and up, 45px lines the heading up with the first header in the
              SupplementalPanel: 15px panel padding + 20px events margin + 10px header margin. */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 min-[1140px]:mt-[45px]">
            <h2 id="program-tabs-heading" className="m-0 text-xl font-bold text-gray-900">
              <T kind="Your Programs" />
            </h2>
            <div
              role="tablist"
              aria-labelledby="program-tabs-heading"
              className="flex flex-wrap gap-1.5"
              data-test-program-tabs={true}
            >
              {programTabKinds.map((kind) => {
                const isSelected = kind === selected;
                return (
                  <button
                    key={kind}
                    ref={(el) => {
                      tabRefs.current[kind] = el;
                    }}
                    type="button"
                    role="tab"
                    id={programTabId(kind)}
                    aria-selected={isSelected}
                    aria-controls={programTabPanelId}
                    tabIndex={isSelected ? 0 : -1}
                    className={clsx(
                      'flex min-h-8 items-center rounded-full border px-3 text-[13px] tracking-normal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                      isSelected
                        ? 'border-primary bg-primary font-semibold text-white'
                        : 'border-gray-300 bg-white font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50',
                    )}
                    onClick={() => {
                      selectTab(kind);
                    }}
                    onKeyDown={(event) => {
                      handleKeyDown(event, kind);
                    }}
                    data-test-program-tab={kind}
                    data-intercom-target={`dashboard-${kind}-tab-button`}
                  >
                    {/* The count is smaller than the label, so they sit on one baseline. */}
                    <span className="flex items-baseline gap-2">
                      <T kind={programTabLabel(kind)} />
                      <span
                        className={clsx(
                          'text-xs font-semibold tabular-nums',
                          isSelected ? 'text-white' : 'text-gray-500',
                        )}
                      >
                        {programTabCount(kind, payload.programs)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div
            role="tabpanel"
            id={programTabPanelId}
            aria-labelledby={programTabId(selected)}
            tabIndex={0}
          >
            <ProgramTabPanel kind={selected} />
          </div>
        </>
      );
    })
    .getOrElse(emptyFragment);
};

export default observer(ProgramTabs);
